import { useState, FormEvent, useMemo } from 'react'
import ct from 'countries-and-timezones'
import { useTranslation, Trans } from 'react-i18next'
import { useFormikContext } from 'formik'
import moment from 'moment-timezone'
import { path } from 'ramda'
import {
  Alert,
  RadioGroup,
  RadioItem,
  Text,
  Box,
  Stack,
  Badge,
  useDialogControllers,
  Notice,
  DateField
} from '@machobear/design-system'
import { vars, breakpoints } from '@machobear/design-system/css'
import { Dropdown } from '@/components/ds-form'
import { useGetProduct, useGetSponsoredListing } from '@/hooks/use-sponsored-listing'
import Skeleton from '@/components/skeleton'
import useMediaQuery from '@/hooks/use-media-query'
import { PopupList } from '@/components/PopupList'
import { FieldTimeInput } from '@/components/form/field-time-picker'
import useGetListingDetails from '@/hooks/use-get-listing-details'
import { checkRestrictedListing } from '@/utils/check-restricted-listing'
import { pushToDatalayer } from '@/utils/push-datalayer'
import { Time, DurationOption, FormValues } from './types'
import { generateHours } from '../../utils/generate-hours'
import { SponsoredListingDetail } from '../../types'
import calculateDailyPrice from '../../utils/calculate-daily-price'

interface CustomWindow extends Window {
  dataLayer: Record<string, unknown>[]
}

declare let window: CustomWindow

const OverlappingNotice = () => {
  const { t } = useTranslation()
  return (
    <Alert tone="caution">
      <Text>
        <Trans i18nKey="l.listing.sponsored_listing.modal.overlap_footer" t={t}>
          This listing is already sponsored on the selected date range.
        </Trans>
      </Text>
    </Alert>
  )
}

const EnoughCreditsFooter = ({ selectedDuration }: { selectedDuration: number }) => {
  const { t } = useTranslation()
  return (
    <Notice tone="info">
      <Text>
        <Trans
          i18nKey="l.listing.sponsored_listing.modal.enough_duration_footer2"
          selectedDuration={selectedDuration}
          t={t}
        >
          {{ selectedDuration }} days of credit will be automatically used on this listing.
        </Trans>
      </Text>
    </Notice>
  )
}

const PreventDelaysFooter = () => {
  const { t } = useTranslation()

  return (
    <Alert tone="info">
      <Text size="small">
        <Trans i18nKey="l.listing.sponsored_listing.modal.delay_footer" t={t}>
          Your listing will not be sponsored until it is published, and{' '}
          <Box
            component="a"
            style={{
              color: vars.foregroundColor.link,
              fontWeight: vars.textWeight.medium,
              pointerEvents: 'auto',
              cursor: 'pointer'
            }}
            href="https://liv.rent/blog/livrent/rental-verification-process/"
            target="_blank"
          >
            verified.
          </Box>
        </Trans>
      </Text>
    </Alert>
  )
}

const NotEnoughCreditsFooter = () => {
  const { t } = useTranslation()
  return (
    <Notice tone="info">
      <Text>
        <Trans i18nKey="l.listing.sponsored_listing.modal.not_enoughduration_footer3" t={t}>
          Not enough credits. Payment is required to continue.
        </Trans>
      </Text>
    </Notice>
  )
}

const getDuration = (value: string | null) => {
  switch (value) {
    case 'seven-days-sponsored-listing-package':
      return '7'
    case 'fourteen-days-sponsored-listing-package':
      return '14'
    case 'thirty-days-sponsored-listing-package':
      return '28'
    default:
      return '7'
  }
}

const getEventId = (value: string | null) => {
  switch (value) {
    case 'seven-days-sponsored-listing-package':
      return 'application-sponsor-listing-modal-7day-pkg'
    case 'fourteen-days-sponsored-listing-package':
      return 'application-sponsor-listing-modal-14day-pkg'
    case 'thirty-days-sponsored-listing-package':
      return 'application-sponsor-listing-modal-30day-pkg'
    default:
      return null
  }
}

// Function to check if the selected date range overlaps with any exists sponsorships
const doDateRangesOverlap = (
  listings: SponsoredListingDetail[],
  start2: Date,
  end2: Date
): boolean => {
  return listings.some((listing: any) => {
    const start1 = moment(listing.planned_start_date).toDate()
    const end1 = moment(listing.planned_end_date).toDate()

    return start1 < end2 && end1 > start2
  })
}
type TimeZoneOptions = Record<string, ct.Timezone>
const timeZoneOptions: TimeZoneOptions = ct.getAllTimezones() ?? []

const tzOptions = Object.values(timeZoneOptions).map(timezone => {
  const offset = moment().isDST() ? timezone?.dstOffsetStr : timezone?.utcOffsetStr
  return {
    value: timezone?.name,
    label: `${timezone?.name.replaceAll('_', ' ')} (UTC${offset})`
  }
})

const SponsoredListingForm = ({
  listingId,
  availableCredits,
  productPackage,
  setProductPackage,
  handleDurationProductChange
}: {
  listingId: string
  availableCredits: number
  productPackage: string | null
  setProductPackage: (a: string | null) => void
  handleDurationProductChange: (id: string | null) => void
}) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.tablet}px)`)
  const [hasOverlappingDates, setHasOverlappingDates] = useState<boolean>(false)
  const hourOptions = useMemo(() => generateHours(5), [])
  const { data: boosts } = useGetSponsoredListing(listingId)

  const { data: listingDetails, isLoading: isLoadingListingDetails }: any =
    useGetListingDetails(listingId)
  const unitNumber = path(['unit', 'unit_number'])(listingDetails)
  const listingAddress = path(['building', 'full_address'])(listingDetails)

  const imported = checkRestrictedListing(listingDetails?.listings?.tags)
  const abbreviation = listingDetails?.listings?.external_info.source_info_abbreviation

  // Get product info

  // Currently calling hook 3 times each with different filters because BE only allows for 1 filter
  // I believe this will be updated in the future so we can reduce this to one call

  const { data: sevenDaysPackage, isLoading: loadingSevenDaysPackage } = useGetProduct(
    'seven-days-sponsored-listing-package'
  )
  const { data: fourteenDaysPackage, isLoading: loadingFourteenDaysPackage } = useGetProduct(
    'fourteen-days-sponsored-listing-package'
  )
  const { data: thirtyDaysPackage, isLoading: loadingThirtyDaysPackage } = useGetProduct(
    'thirty-days-sponsored-listing-package'
  )

  const isProductLoading =
    loadingSevenDaysPackage || loadingFourteenDaysPackage || loadingThirtyDaysPackage

  const { values, setFieldValue } = useFormikContext<FormValues>()
  const { visible, showModal, closeModal } = useDialogControllers(
    { time: false },
    { stopPropagation: true }
  )

  const selectedDuration = getDuration(productPackage)
  const sponsored: SponsoredListingDetail[] = boosts
    ? Object.values(boosts?.sponsored_listings)
    : []

  const completeListingAddress = unitNumber
    ? `${unitNumber} - ${listingAddress}`
    : `${listingAddress}`

  const renderDurationFooter = (): React.ReactNode => {
    const duration = parseInt(selectedDuration)

    if (availableCredits >= duration) {
      return <EnoughCreditsFooter selectedDuration={duration} />
    } else {
      return <NotEnoughCreditsFooter />
    }
  }

  const calculateEndDate = (startDate: Date, timezone: string) => {
    const dur = parseInt(selectedDuration)
    const ed = moment(startDate).add(dur, 'days').tz(timezone)

    return ed
  }

  const getProductDetails = (planName: string, products: any[] | undefined): DurationOption => {
    const product = products ? products[0] : null
    const duration = getDuration(planName)
    if (product && duration) {
      const { regular_plan_price } = product
      const dailyPrice = calculateDailyPrice(parseFloat(regular_plan_price), parseFloat(duration))

      return {
        value: planName,
        label: t(`l.listing.sponsored_listing.duration.${planName}`, `${duration} Days`),
        secondaryLabel: `$${dailyPrice}/day`,
        tertiaryLabel: `$${regular_plan_price}`
      }
    }

    // Handle the case where the product is not found
    return {
      value: '',
      label: '',
      secondaryLabel: '',
      tertiaryLabel: ''
    }
  }

  const durationOptions = [
    getProductDetails('seven-days-sponsored-listing-package', sevenDaysPackage?.products),
    getProductDetails('fourteen-days-sponsored-listing-package', fourteenDaysPackage?.products),
    getProductDetails('thirty-days-sponsored-listing-package', thirtyDaysPackage?.products)
  ]

  const handleSponsorshipFieldChange = (
    startDate: Date,
    plannedStartTime: Time,
    timezone: string
  ) => {
    const st = moment().tz(timezone).set({
      date: startDate.getDate(),
      month: startDate.getMonth(),
      year: startDate.getFullYear(),
      hour: plannedStartTime?.hour,
      minute: plannedStartTime?.minute,
      second: 0
    })

    const ed = calculateEndDate(new Date(st.toString()), timezone)
    const overlap = doDateRangesOverlap(sponsored, new Date(st.toString()), new Date(ed.toString()))

    if (overlap) {
      pushToDatalayer({
        event: 'app_sponsor_warn_alert'
      })
    }

    setFieldValue('planned_end_date', ed)
    setHasOverlappingDates(overlap)
  }

  const handleDurationChange = async (durationValue: string | undefined) => {
    await setFieldValue('duration', durationValue)
    setProductPackage(durationValue ?? null)
    handleDurationProductChange(durationValue ?? null)

    if (values.planned_start_date !== null) {
      handleSponsorshipFieldChange(
        values.planned_start_date,
        values.planned_start_time,
        values.timezone
      )
    }

    const eventId = getEventId(durationValue ?? null)
    pushToDatalayer({
      event: eventId
    })
  }

  const handleImmediateStartChange = (e: FormEvent<HTMLInputElement>) => {
    setFieldValue('immediate_start', e.currentTarget.value)

    if (e.currentTarget.value === 'true') {
      handleSponsorshipFieldChange(new Date(), values.planned_start_time, values.timezone)
    } else {
      handleSponsorshipFieldChange(
        values?.planned_start_date,
        values.planned_start_time,
        values.timezone
      )
    }
  }

  const handleTimezoneChange = async (e: string | undefined) => {
    await setFieldValue('timezone', e)
    handleSponsorshipFieldChange(values?.planned_start_date, values.planned_start_time, e ?? '')
  }

  const handleStartDateChange = async (value: Date | null) => {
    await setFieldValue('planned_start_date', value)
    if (value !== null) {
      handleSponsorshipFieldChange(value, values.planned_start_time, values.timezone)
    }
  }

  const showImmediateOverlapping =
    values?.immediate_start === 'true' && hasOverlappingDates && values?.duration !== ''

  return (
    <Box position="relative" style={{ width: isMobile ? '100%' : '600px' }}>
      <Box paddingY="gutter" borderTop="borderNeutralLight">
        <Box paddingBottom="gutter">
          {isProductLoading || isLoadingListingDetails ? (
            <Skeleton height={24} />
          ) : (
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              justifyContent="spaceBetween"
              alignItems="center"
              gap="small"
            >
              <Box>
                <Text weight="medium">{completeListingAddress}</Text>
              </Box>
              <Box>
                <Badge tone="neutral">
                  {t(
                    'l.listing.sponsored_listing.modal.available_title',
                    `Available Credits: {{ availableCredits }} days`,
                    { availableCredits }
                  )}
                </Badge>
              </Box>
            </Box>
          )}
        </Box>

        <Stack space="xsmall">
          <Text size="medium" weight="medium" id="sponseredListingDuration">
            {t('l.listing.sponsored_listing.modal.duration_title', '1. Select duration')}
          </Text>
          <Box marginLeft="gutter" marginBottom="small">
            <Stack space="small">
              {isProductLoading ? (
                <Skeleton height={58} />
              ) : (
                <Dropdown
                  aria-labelledby="sponseredListingDuration"
                  label={t('l.listing.sponsored_listing.modal.duration_label', 'Duration')}
                  name="duration"
                  required
                  options={durationOptions}
                  onChange={handleDurationChange}
                />
              )}
              {!!values?.duration && <Box>{renderDurationFooter()}</Box>}
            </Stack>
          </Box>
        </Stack>
        <Stack space="xsmall">
          <Box>
            <Text size="medium" weight="medium">
              {t('l.listing.sponsored_listing.modal.start_date_title', '2. Select start date')}
            </Text>
          </Box>
          <Box marginLeft="gutter">
            <Stack space="small">
              {isProductLoading ? (
                <Skeleton height={58} />
              ) : (
                <Box>
                  <RadioGroup
                    id="immediate_start"
                    label=""
                    tone="neutral"
                    value={values.immediate_start}
                    onChange={handleImmediateStartChange}
                  >
                    <RadioItem
                      value="true"
                      label={t(
                        'l.listing.sponsored_listing.modal.immediately_label',
                        'Immediately'
                      )}
                    >
                      {showImmediateOverlapping && (
                        <Box marginTop="xsmall">
                          <OverlappingNotice />
                        </Box>
                      )}
                    </RadioItem>

                    <RadioItem
                      value="false"
                      label={t(
                        'l.listing.sponsored_listing.modal.schedule_later_label',
                        'Schedule for later'
                      )}
                    />
                  </RadioGroup>

                  {values?.immediate_start === 'false' && (
                    <Stack space="small">
                      <Box
                        display="flex"
                        flexDirection={isMobile ? 'column' : 'row'}
                        gap="medium"
                        marginTop="xsmall"
                        marginLeft="xlarge"
                      >
                        <Box width="full">
                          <DateField
                            id="planned_start_date"
                            name="planned_start_date"
                            value={values?.planned_start_date}
                            label={t(
                              'l.listing.sponsored_listing.modal.start_date_label',
                              'Start date'
                            )}
                            onChange={handleStartDateChange}
                            required
                            disabled={false}
                            minDate={moment().toDate()}
                          />
                        </Box>

                        <Box width="full" position="relative">
                          <FieldTimeInput
                            name="planned_start_time"
                            label={t('l.listing.sponsored_listing.modal.time_label', 'Time')}
                            required
                            onAction={visible.time ? closeModal('time') : showModal('time')}
                            onChange={(timeValue: { hour: number; minute: number }) => {
                              setFieldValue('planned_start_time', timeValue)
                            }}
                          />
                          <PopupList
                            open={visible.time}
                            selectedValue={values?.planned_start_time}
                            onClose={closeModal('time')}
                            options={hourOptions ?? []}
                            onChange={(timeValue: { hour: number; minute: number }) => {
                              setFieldValue('planned_start_time', timeValue)
                            }}
                          />
                        </Box>
                      </Box>
                      {hasOverlappingDates && (
                        <Box marginLeft="xlarge">
                          <OverlappingNotice />
                        </Box>
                      )}
                      <Box marginLeft="xlarge">
                        <Dropdown
                          name="timezone"
                          label={t('l.listing.sponsored_listing.timezone.text', 'Time zone')}
                          onChange={handleTimezoneChange}
                          required
                          options={tzOptions}
                        />
                      </Box>
                    </Stack>
                  )}
                </Box>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
      <Box marginBottom="gutter">
        <Stack space="gutter" align="left">
          <PreventDelaysFooter />
          {imported && !isLoadingListingDetails && (
            <Box maxWidth="small">
              <Stack space="large">
                <Text tone="tertiary" size="xsmall">
                  <Trans
                    i18nKey="l.listing.sponsored_listing.imported.disclaimer.text1"
                    values={{
                      abbreviation
                    }}
                  >
                    By continuing, I agree to sponsor this listing on liv.rent and acknowledge that
                    the data originates from {{ abbreviation }}, which retains proprietary rights
                    over the MLS® data and listing information. The listing will now be hosted on
                    liv.rent, making it the primary source of the listing.
                  </Trans>
                </Text>
                <Text tone="tertiary" size="xsmall">
                  <Trans
                    i18nKey="l.listing.sponsored_listing.imported.disclaimer.text2"
                    values={{
                      abbreviation
                    }}
                  >
                    I confirm that I have permission from the owner or authorized representative to
                    sponsor the listing. I agree to comply with {{ abbreviation }}’s MLS® Rules and
                    accept responsibility for ensuring compliance with all applicable laws and
                    agreements, indemnifying liv.rent and {{ abbreviation }} against any claims
                    arising from unauthorized use.
                  </Trans>
                </Text>
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  )
}

export default SponsoredListingForm
